import React, { Fragment, useState, useEffect } from "react";
import { backend, API_URL } from "../../../config";
import {
  Button,
  Typography,
  Modal,
  CircularProgress,
  Container,
  Grid,
  TextField,
  InputLabel,
  Autocomplete,
} from "@mui/material";

import WarningIcon from "@mui/icons-material/Warning";

import DatePicker from "@mui/lab/DatePicker";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";

import jsPDF from "jspdf";
import "jspdf-autotable";

import Logo from "../../../assets/images/logo.png";
import ManagementNav from "../../../features/OrderManagement/components/Nav/ManagementNav";

import { makeStyles } from "@mui/styles";

const useStyles = makeStyles((theme) => ({
  appBar: {
    position: "relative",
  },
  layout: {
    width: "auto",
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    [theme.breakpoints.up(600)]: {
      width: 900,
      marginLeft: "auto",
      marginRight: "auto",
    },
  },
  paper: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(5),
    [theme.breakpoints.up(600 + theme.spacing(3) * 2)]: {
      marginTop: theme.spacing(6),
      marginBottom: theme.spacing(6),
      padding: theme.spacing(3),
    },
  },
  stepper: {
    padding: theme.spacing(3, 0, 5),
  },
  buttons: {
    display: "flex",
    justifyContent: "flex-end",
  },
  button: {
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(1),
  },
  modal: {
    position: "absolute",
    width: 400,
    backgroundColor: "#fff",
    border: "1px solid rgba(0,0,0,.2)",
    borderRadius: ".3rem",
    boxShadow: theme.shadows[5],
    padding: "1rem",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
}));

export const generateDocument = (ordersData) => {
  /* const ffAddress = [
    "Friendly Foods, Inc.",
    "3336 S La Cienega Blvd Ste 113",
    "Los Angeles, CA 90016",
    "323-653-5900",
  ]; */

  const ffAddress = [
    "Friendly Foods, Inc.",
    "323-653-5900",
    "626-788-3663-Text",
  ];

  const dDate = ordersData[0]?.order?.delivery_date || "";
  const dateObj =
    dDate !== "" ? new Date(ordersData[0]?.order?.delivery_date) : new Date();

  const dateString = new Date(
    dateObj.getTime() - dateObj.getTimezoneOffset() * 60000,
  )
    .toISOString()
    .split("T")[0];

  const dateOrderHeaders = [["Date", "Order #"]];
  const shipToHeaders = [["Ship To"]];
  const noteHeaders = [["Note"]];

  const itemsHeaders = [
    ["Bin", "Item Name", "Item Code", "Unit Qty", "Case Qty"],
  ];

  let picklistsData = ordersData.map((item) => {
    const { order, orderItems } = item;

    let deliveryDateObj = new Date(order.delivery_date);

    const deliveryDateString = new Date(
      deliveryDateObj.getTime() - deliveryDateObj.getTimezoneOffset() * 60000,
    )
      .toISOString()
      .split("T")[0];

    const shipTo = `${
      order?.company_location?.name || order?.customer.username
    }\n${order?.company_location?.physical_address?.complete_address}`;

    let orderObj = {
      date: deliveryDateString,
      order: order.id,
      address: shipTo,
      note: order.note,
    };

    const orderItemsData = orderItems.map((oiItem) => {
      const bin =
        oiItem.bin_override !== ""
          ? oiItem.bin_override
          : oiItem?.product_data?.bin || "";

      const itemName =
        oiItem?.product_data?.name +
          (oiItem?.product_data?.weight_type === "r/w" ? "|isCW" : "") || "";

      const itemCode =
        oiItem?.product_data?.item_code?.item_code +
          (oiItem?.product_data?.weight_type === "r/w" ? "|isCW" : "") || "";

      const unitQty = oiItem.out_of_stock
        ? "Ø"
        : `U: ${
            oiItem.unit_quantity === oiItem.units_ordered
              ? oiItem.unit_quantity
              : `${oiItem.unit_quantity} / ${oiItem.units_ordered}`
          }`;

      const caseQty = oiItem.out_of_stock
        ? "Ø"
        : `C: ${
            oiItem.case_quantity === oiItem.cases_ordered
              ? oiItem.case_quantity
              : `${oiItem.case_quantity} / ${oiItem.cases_ordered}`
          }`;

      return [bin, itemName, itemCode, unitQty, caseQty];
    });

    orderObj.orderItems = orderItemsData;

    return orderObj;
  });

  // PDF
  const pdf = new jsPDF("p", "mm", "a4", false);

  pdf.autoTableSetDefaults({
    headStyles: { fillColor: [161, 49, 39] },
  });

  const pageSize = pdf.internal.pageSize;
  const paginationX = pdf.internal.pageSize.width - 27;
  const paginationY =
    (pageSize.height ? pageSize.height : pageSize.getHeight()) - 10;

  let lastPageCount = 0;
  picklistsData.forEach((item, index) => {
    const dateOrderData = [[item.date, item.order]];
    const shipToData = [[item.address]];
    const noteData = [[item.note]];

    pdf.addImage(Logo, "PNG", 15, 15, 40, 26);
    pdf.setFontSize(10);
    pdf.text(ffAddress, 60, 20);

    pdf.setFontSize(14);
    pdf.text("Delivery Ticket", 150, 15);

    pdf.autoTable({
      head: dateOrderHeaders,
      body: dateOrderData,
      startY: 17,
      margin: { left: 150 },
      styles: { overflow: "hidden", cellWidth: "wrap" },
    });

    pdf.autoTable({
      head: shipToHeaders,
      body: shipToData,
      startY: 17,
      margin: { left: 100, right: 65 },
    });

    if (item.note !== "") {
      pdf.autoTable({
        head: noteHeaders,
        body: noteData,
        startY: pdf.lastAutoTable.finalY + 5,
      });
    }

    pdf.autoTable({
      head: itemsHeaders,
      body: item.orderItems,
      startY: pdf.lastAutoTable.finalY + 5,
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "left" },
      },
      styles: {
        halign: "center",
        valign: "middle",
      },
      didParseCell: (data) => {
        if (data.cell.raw.includes("|isCW")) {
          data.cell.styles.fontStyle = "bold";
          data.cell.text = [data.cell.text[0].replace("|isCW", "")];
        }
      },
    });

    let defaultStartX = pdf.internal.pageSize.width / 2 - 20;
    let defaultStartY = pdf.lastAutoTable.finalY + 2;

    pdf.setFontSize(6);
    pdf.rect(defaultStartX, defaultStartY, 50, 20);
    pdf.setFont("helvetica", "italic");
    pdf.text("Customer Signature", defaultStartX + 15, defaultStartY + 19);
    pdf.setFont("helvetica", "normal");

    const pageCount = pdf.internal.getNumberOfPages();

    const diffCount = pageCount - lastPageCount;

    for (let i = 1; i <= diffCount; i++) {
      pdf.setPage(lastPageCount + i);
      pdf.text(`Page ${i} of ${diffCount}`, paginationX, paginationY);
    }

    lastPageCount = pdf.internal.getCurrentPageInfo().pageNumber;

    if (index !== ordersData.length - 1) {
      pdf.addPage("p", "mm", "a4", false);
    }
  });

  const pdfOutput = pdf.output("blob");
  const pdfFile = new File([pdfOutput], `picklists-${dateString}.pdf`, {
    type: "application/pdf",
  });

  return pdfFile;
};

export const PrintPicklists = (props) => {
  const classes = useStyles();

  const cDate = new Date();

  const [confirmModal, setConfirmModal] = useState(false);
  const [errorInfo, setErrorInfo] = useState({ error: false, message: "" });
  const [loadingFlag, setLoadingFlag] = useState(false);
  const [finishedFlag, setFinishedFlag] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState(cDate);
  const [orders, setOrders] = useState([]);
  const [ordersSelected, setOrdersSelected] = useState([]);
  const [ordersNotPicked, setOrdersNotPicked] = useState([]);

  const handleChangeOrders = (e, value) => {
    setOrdersSelected(value);
  };

  const handleChangeDate = (e) => {
    const { value } = e.target;
    setOrders([]);
    setOrdersSelected([]);
    setDeliveryDate(value);
  };

  const warningFormat = () => {
    return ordersNotPicked.length ? (
      <>
        <div style={{ color: "red" }}>
          <b>The following orders have not completed picking yet:</b>
          {` ${ordersNotPicked.join(", ")}`}
        </div>
        <br />
        <br />
      </>
    ) : (
      <></>
    );
  };

  const toggleConfirmModal = () => setConfirmModal(!confirmModal);

  const confirmModalContent = (
    <div className={classes.modal}>
      {!loadingFlag && !finishedFlag ? (
        <>
          Are you sure you want to bulk generate invoices?
          <br />
          <br />
          {warningFormat()}
          <div align="right">
            <Button
              onClick={() => toggleConfirmModal()}
              className={classes.button}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => sendRequest()}
              className={classes.button}
            >
              Confirm
            </Button>
          </div>
        </>
      ) : loadingFlag ? (
        <>
          <div align="center">
            <CircularProgress />
            <div>
              Generating Invoices <br />
              This may take some time relative to the number of invoices
            </div>
          </div>
          {errorInfo.error ? (
            <>
              <br />
              <br />
              There was an error while generating invoices
              <br />
              <br />
              <span
                style={{
                  color: "red",
                  wordBreak: "break-word",
                  maxHeight: 160,
                  overflow: "scroll",
                  display: "inline-block",
                  width: "100%",
                }}
              >
                <b>Error:</b>
                <br />
                <div dangerouslySetInnerHTML={{ __html: errorInfo.message }} />
              </span>
              <div align="right">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => resolveRequest()}
                  className={classes.button}
                >
                  Continue
                </Button>
              </div>
            </>
          ) : (
            <></>
          )}
        </>
      ) : (
        <>
          The invoices were generated successfully.
          <div align="right">
            <Button
              variant="contained"
              color="primary"
              onClick={() => resolveRequest()}
              className={classes.button}
            >
              Close
            </Button>
          </div>
        </>
      )}
    </div>
  );

  const resolveRequest = () => {
    setLoadingFlag(false);
    setFinishedFlag(false);
    toggleConfirmModal();
    setErrorInfo({ error: false, message: "" });
  };

  const sendRequest = async () => {
    setLoadingFlag(true);

    Promise.all(
      ordersSelected.map(
        async (orderObj) =>
          await backend
            .get(
              `${API_URL}orderitems/?order=${orderObj.id}&product_detail=True&no_page=1`,
            )
            .then(({ data }) => ({ order: orderObj, orderItems: data }))
            .catch((error) => {
              console.log(error);
              const errorData = error.response.data;

              setErrorInfo((prevState) => {
                let newMsg =
                  typeof errorData === "object"
                    ? errorData.hasOwnProperty("message")
                      ? errorData.message
                      : errorData.detail
                    : error.message;

                if (newMsg === undefined) {
                  const orderId = orderObj.id;

                  if (!prevState.message.includes(orderId)) {
                    newMsg = `Invoice ${orderId}: ${error.response.statusText}`;
                  } else {
                    newMsg = "";
                  }
                }

                if (newMsg !== "") {
                  const errorMsg =
                    prevState.message +
                    `${prevState.message === "" ? "" : "<br />"}` +
                    newMsg;

                  return {
                    error: true,
                    message: errorMsg,
                  };
                }

                return prevState;
              });
            }),
      ),
    ).then(async (result) => {
      const pdfFile = generateDocument(result);
      const fileURL = URL.createObjectURL(pdfFile);

      window.open(fileURL);

      setLoadingFlag(false);
      setFinishedFlag(true);
    });
  };

  useEffect(() => {
    const getOrders = async () => {
      let deliveryDateObj = new Date(deliveryDate);

      const deliveryDateParam = new Date(
        deliveryDateObj.getTime() - deliveryDateObj.getTimezoneOffset() * 60000,
      )
        .toISOString()
        .split("T")[0];

      await backend
        .get(
          `${API_URL}orders?no_page=1&start_date=${deliveryDateParam}&end_date=${deliveryDateParam}&include_company_location_addresses=True`,
        )
        .then(({ data }) => {
          if (data.length) {
            let oNotPicked = [];

            data.forEach((item) => {
              if (!item.picking_complete) {
                oNotPicked.push(
                  `${item.customer ? item.customer.username : "Anonymous"} #${
                    item.id
                  }`,
                );
              }
            });

            setOrders(data);
            setOrdersSelected(data);
            setOrdersNotPicked(oNotPicked);
          }
        });
    };

    if (deliveryDate !== null) {
      getOrders();
    }
  }, [deliveryDate]);

  return (
    <Container style={{ minHeight: "80vh" }}>
      <Modal open={confirmModal} onClose={toggleConfirmModal}>
        {confirmModalContent}
      </Modal>
      <ManagementNav {...props} />
      <Typography component="h6" variant="h6" align="center">
        Print Picklists
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12}></Grid>
        <Grid item xs={12} sm={12}>
          Select the orders you would like to generate invoice for, not picked
          complete orders are not pre-selected
        </Grid>
        <Grid item xs={12} sm={12}></Grid>
        <Grid item xs={12} sm={12}>
          Load orders from a given date.
        </Grid>
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              disableToolbar
              variant="inline"
              format="MM/dd/yyyy"
              margin="normal"
              KeyboardButtonProps={{
                "aria-label": "change date",
              }}
              value={deliveryDate}
              renderInput={(props) => (
                <TextField
                  {...props}
                  fullWidth
                  label="Delivery date"
                  variant="standard"
                />
              )}
              onChange={(date) =>
                handleChangeDate({
                  target: {
                    name: "delivery_date",
                    value: date,
                  },
                })
              }
            />
          </LocalizationProvider>
        </Grid>
        {deliveryDate !== null ? (
          <>
            <Grid item xs={12} sm={12}>
              <InputLabel shrink id="orders">
                Orders
              </InputLabel>
              <Autocomplete
                fullWidth
                multiple
                disablePortal
                size="small"
                name="orders"
                options={orders}
                getOptionLabel={(option) =>
                  `${
                    option.customer ? option.customer.username : "Anonymous"
                  } #${option.id}`
                }
                renderInput={(params) => (
                  <TextField {...params} size="small" variant="standard" />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    {`${
                      option.customer ? option.customer.username : "Anonymous"
                    } #${option.id}`}
                    {!option.picking_complete && (
                      <WarningIcon
                        color="warning"
                        label="Not picked complete"
                      />
                    )}
                  </li>
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(e, value) => handleChangeOrders(e, value)}
                helpertext="Select the orders to include in the receipt difference."
                value={ordersSelected}
              />
            </Grid>
            {ordersNotPicked.length > 0 && (
              <Grid item xs={12}>
                <b>The following orders have not completed picking yet:</b>
                {` ${ordersNotPicked.join(", ")}`}
              </Grid>
            )}
          </>
        ) : (
          <></>
        )}
        <Grid item xs={12} sm={12} style={{ textAlign: "center" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => toggleConfirmModal()}
            className={classes.button}
            disabled={!ordersSelected.length || loadingFlag}
          >
            Generate Print Format
          </Button>
        </Grid>
      </Grid>
    </Container>
  );
};

export default PrintPicklists;
